import React, { useState, useEffect } from "react";
import { Plus } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import DeleteModal from "../../components/models/deleteModal.jsx";
import CreateDataBaseModal from "../../components/models/addEmployee";
import ResetPasswordModal from "../../components/models/resetPassword";
import api from "../../services/apiService";
import UserTable from "../../components/employee-management/UserTable";
import AlertNotification from "../../components/notification";
import { DataTableButton, DataTableFilters, DataTableSelect } from "../../components/data-table";

function EmployeeMangement() {
  const getDisplayNameFromEmail = (emailValue) => {
    const email = String(emailValue || "").trim();
    if (!email.includes("@")) return "-";

    const localPart = email.split("@")[0] || "";
    if (!localPart) return "-";

    return localPart
      .split(/[._-]+/)
      .filter(Boolean)
      .map((chunk) => chunk.charAt(0).toUpperCase() + chunk.slice(1))
      .join(" ");
  };

  const normalizeStatus = (isActive) => {
    return isActive ? "Active" : "Inactive";
  };

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [addNewEmployeeShowModal, setAddNewEmployeeShowModal] = useState(false);
  const [isDeletePopupVisible, setDeletePopupVisible] = useState(false);
  const [employeeModalMode, setEmployeeModalMode] = useState("add");
  const [resetPasswordUser, setResetPasswordUser] = useState(null);
  const [showResetPasswordModal, setShowResetPasswordModal] = useState(false);
  const [notification, setNotification] = useState(null);
  const [roleFilter, setRoleFilter] = useState("");
  const location = useLocation();
  const navigate = useNavigate();

  // Dashboard quick action: open the add-user modal, then clear the flag so a
  // refresh or back-navigation doesn't reopen it.
  useEffect(() => {
    if (!location.state?.openCreate) return;
    setSelectedUser(null);
    setEmployeeModalMode("add");
    setAddNewEmployeeShowModal(true);
    navigate(location.pathname, { replace: true, state: null });
  }, [location.state, location.pathname, navigate]);
  const [statusFilter, setStatusFilter] = useState("");

  useEffect(() => {
    fetchEmployees();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchEmployees = async () => {
    try {
      setLoading(true);
      const res = await api.get("/fetch-users");
      const usersData = (res.data?.data || []).map((user) => ({
        id: user.id,
        userId: user.id,
        firstName: user.firstName || user.firstname || "",
        lastName: user.lastName || user.lastname || "",
        Name:
          `${user.firstName || user.firstname || ""} ${user.lastName || user.lastname || ""}`.trim() ||
          getDisplayNameFromEmail(user.email),
        Email: user.email || "N/A",
        Role: user.role || "N/A",
        Status: normalizeStatus(Boolean(user.isActive)),
        JoiningDate: user.createdAt || "N/A",
        avatarThumbUrl: user.avatarThumbUrl || null,
        LinkedEmployee: user.employeeProfile || null,
      }));

      setUsers(usersData);
    } catch (error) {
      console.error("Error fetching employee data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleCloseDeletePopup = () => setDeletePopupVisible(false);

  const handleConfirmDelete = async () => {
    const employeeId = selectedUser?.id || selectedUser?.userId;
    if (!employeeId) {
      console.error("No employee id found for delete action.");
      setNotification({ text: "Unable to delete user: missing user id.", status: false });
      return;
    }

    try {
      await api.delete(`/delete-user/${employeeId}`);
      await fetchEmployees();
      setDeletePopupVisible(false);
      setSelectedUser(null);
      setNotification({ text: "User deleted successfully!", status: true });
    } catch (error) {
      console.error("Error deleting employee:", error);
      setNotification({ text: "Failed to delete user.", status: false });
    }
  };

  const handleEditUser = (user) => {
    setSelectedUser(user);
    setEmployeeModalMode("edit");
    setAddNewEmployeeShowModal(true);
  };

  const handleDeleteUser = (user) => {
    setSelectedUser(user);
    setDeletePopupVisible(true);
  };

  const handleResetPassword = (user) => {
    setResetPasswordUser(user);
    setShowResetPasswordModal(true);
  };

  const availableRoles = [...new Set(users.map((user) => user.Role).filter(Boolean))];

  // Search is handled by the table; role/status filters stay page-owned.
  const filteredUsers = users.filter((user) => {
    const matchesRole = !roleFilter || user.Role === roleFilter;
    const matchesStatus = !statusFilter || user.Status === statusFilter;
    return matchesRole && matchesStatus;
  });

  return (
    <div className="w-full max-w-full">
      <UserTable
        users={filteredUsers}
        loading={loading}
        onEditUser={handleEditUser}
        onDeleteUser={handleDeleteUser}
        onResetPassword={handleResetPassword}
        activeFilterCount={[roleFilter, statusFilter].filter(Boolean).length}
        filterContent={
          <DataTableFilters
            canClear={Boolean(roleFilter || statusFilter)}
            onClear={() => {
              setRoleFilter("");
              setStatusFilter("");
            }}
          >
            <DataTableSelect
              id="users-role-filter"
              label="Role"
              value={roleFilter}
              onChange={setRoleFilter}
              options={[{ value: "", label: "All Roles" }, ...availableRoles.map((r) => ({ value: r, label: r }))]}
            />
            <DataTableSelect
              id="users-status-filter"
              label="Status"
              value={statusFilter}
              onChange={setStatusFilter}
              options={[
                { value: "", label: "All Status" },
                { value: "Active", label: "Active" },
                { value: "Inactive", label: "Inactive" },
              ]}
            />
          </DataTableFilters>
        }
        toolbarRight={
          <DataTableButton
            variant="primary"
            icon={Plus}
            onClick={() => {
              setSelectedUser(null);
              setEmployeeModalMode("add");
              setAddNewEmployeeShowModal(true);
            }}
          >
            Add User
          </DataTableButton>
        }
      />

      <CreateDataBaseModal
        show={addNewEmployeeShowModal}
        mode={employeeModalMode}
        user={selectedUser}
        onSuccess={fetchEmployees}
        handleClose={() => {
          setAddNewEmployeeShowModal(false);
          setSelectedUser(null);
          setEmployeeModalMode("add");
        }}
      />
      <DeleteModal
        show={isDeletePopupVisible}
        handleClose={handleCloseDeletePopup}
        handleConfirm={handleConfirmDelete}
        title="Delete user"
        message={`Are you sure you want to delete ${selectedUser?.Name}?`}
      />
      <ResetPasswordModal
        show={showResetPasswordModal}
        user={resetPasswordUser}
        handleClose={() => {
          setShowResetPasswordModal(false);
          setResetPasswordUser(null);
        }}
        onSuccess={() =>
          setNotification({ text: "Password updated successfully!", status: true })
        }
      />
      {notification && (
        <AlertNotification
          text={notification.text}
          status={notification.status}
          onClose={() => setNotification(null)}
        />
      )}
    </div>
  );
}

export default EmployeeMangement;
